import { useEffect } from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import {
  MapPin,
  CheckCircle2,
  ArrowRight,
  MessageCircle,
  Star,
  TrendingUp,
  ScanSearch,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";
import FloatingWA from "@/components/sections/FloatingWA";
import Pricing from "@/components/sections/Pricing";
import Process from "@/components/sections/Process";
import Testimonials from "@/components/sections/Testimonials";
import FAQ from "@/components/sections/FAQ";
import { getCityBySlug, cities } from "@/content/cities";

function openAudit() {
  window.dispatchEvent(new CustomEvent("open-audit-modal"));
}

export default function CityLanding() {
  const location = useLocation();
  // Extract slug from URL /jasa-website-{slug}
  const slug = location.pathname.replace(/^\/jasa-website-/, "");
  const city = getCityBySlug(slug);

  useEffect(() => {
    if (city) {
      document.title = city.seoTitle;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", city.seoDescription);

      let keywords = document.querySelector('meta[name="keywords"]');
      if (!keywords) {
        keywords = document.createElement("meta");
        keywords.setAttribute("name", "keywords");
        document.head.appendChild(keywords);
      }
      keywords.setAttribute("content", city.keywords);
    }
    window.scrollTo(0, 0);
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Agung Darmawan — Jasa Website UMKM ${city.name}`,
    description: city.seoDescription,
    areaServed: {
      "@type": "City",
      name: city.name,
    },
    provider: {
      "@type": "Person",
      name: "Agung Darmawan",
    },
    telephone: "+628211001473",
    priceRange: "Rp 1.500.000 - Rp 15.000.000",
    url: `https://agungwebdev.com/jasa-website-${city.slug}`,
  };

  const waMessage = encodeURIComponent(
    `Halo Kak Agung, saya UMKM di ${city.name} mau konsultasi jasa pembuatan website. Bisa dibantu?`
  );

  return (
    <div className="min-h-screen bg-[var(--brand-cream)]">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden bg-[var(--brand-ink)] text-white">
        <div className="absolute inset-0 dotted-grid opacity-10 pointer-events-none" />
        <div className="pointer-events-none absolute top-20 -right-24 w-96 h-96 rounded-full bg-[var(--brand-azure)] blur-3xl opacity-20" />
        <div className="pointer-events-none absolute -bottom-20 -left-24 w-96 h-96 rounded-full bg-[var(--brand-gold)] blur-3xl opacity-15" />

        <div className="relative mx-auto max-w-7xl px-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold">
            <MapPin className="w-3.5 h-3.5 text-[var(--brand-gold)]" />
            {city.tagline}
          </div>
          <h1
            data-testid={`city-h1-${city.slug}`}
            className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.02]"
          >
            {city.heroTitle}
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/75 leading-relaxed">
            {city.heroSubtitle}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`https://wa.me/628211001473?text=${waMessage}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`city-cta-wa-${city.slug}`}
              className="cta-shine inline-flex items-center gap-2 rounded-full bg-[var(--brand-gold)] text-[var(--brand-ink)] px-6 py-3.5 text-sm font-bold hover:bg-[#ffcc3b] transition"
            >
              <MessageCircle className="w-4 h-4" />
              Konsultasi WhatsApp
            </a>
            <button
              onClick={openAudit}
              data-testid={`city-cta-audit-${city.slug}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 backdrop-blur px-6 py-3.5 text-sm font-semibold hover:bg-white/10 transition"
            >
              <ScanSearch className="w-4 h-4" />
              Audit Gratis
            </button>
          </div>

          {/* Quick stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {[
              { n: "Mulai", v: "Rp 1,5jt" },
              { n: "Selesai", v: "3-5 hari" },
              { n: "Garansi", v: "30 hari" },
              { n: `Diskon ${city.name}`, v: "10%" },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-4">
                <div className="text-xs uppercase tracking-wider text-white/60">{s.n}</div>
                <div className="mt-1 text-xl md:text-2xl font-bold text-[var(--brand-gold)]">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-5">
          <div className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-azure-soft)] px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-azure)]">
            <Sparkles className="w-3.5 h-3.5" />
            Kenapa Pilih Saya
          </div>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[var(--brand-ink)]">
            Web developer lokal untuk<br className="hidden md:block" /> UMKM di {city.nameFull}
          </h2>
          <p className="mt-5 text-lg text-[var(--brand-gray-500)] leading-relaxed">
            {city.intro}
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {city.localBenefits.map((b, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-[var(--brand-border)]"
              >
                <span className="shrink-0 h-8 w-8 rounded-lg bg-[var(--brand-azure-soft)] text-[var(--brand-azure)] flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" />
                </span>
                <p className="text-sm text-[var(--brand-gray-700)] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>

          {/* Case highlight */}
          <div className="mt-10 rounded-3xl bg-[var(--brand-ink)] text-white p-8 lg:p-10 relative overflow-hidden">
            <div className="absolute inset-0 dotted-grid opacity-10" />
            <div className="relative">
              <div className="flex items-center gap-2 text-[var(--brand-gold)] text-xs font-bold uppercase tracking-[0.25em]">
                <TrendingUp className="w-4 h-4" />
                Studi Kasus {city.name}
              </div>
              <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold leading-tight">
                {city.caseHighlight.title}
              </h3>
              <p className="mt-3 text-white/75 leading-relaxed">{city.caseHighlight.desc}</p>
              <div className="mt-4 flex items-center gap-1 text-[var(--brand-gold)]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
                <span className="ml-2 text-xs text-white/60">Klien real, testimoni tersedia</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reuse Pricing, Process, Testimonials, FAQ from homepage */}
      <Pricing />
      <Process />
      <Testimonials />
      <FAQ />

      {/* Bottom CTA */}
      <section className="py-20 bg-[var(--brand-cream)]">
        <div className="mx-auto max-w-4xl px-5 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-[var(--brand-ink)] leading-tight">
            Siap punya website profesional<br />
            untuk bisnis Anda di {city.name}?
          </h2>
          <p className="mt-4 text-[var(--brand-gray-500)] max-w-xl mx-auto">
            Konsultasi gratis, tanpa komitmen. Saya bantu review kebutuhan dan
            rekomendasi paket paling cocok untuk UMKM {city.name} Anda.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`https://wa.me/628211001473?text=${waMessage}`}
              target="_blank"
              rel="noreferrer"
              className="cta-shine inline-flex items-center gap-2 rounded-full bg-[var(--brand-ink)] text-white px-6 py-3.5 text-sm font-bold hover:bg-[var(--brand-ink-2)] transition"
            >
              <MessageCircle className="w-4 h-4" />
              Chat via WhatsApp
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-ink)] text-[var(--brand-ink)] px-6 py-3.5 text-sm font-semibold hover:bg-[var(--brand-ink)] hover:text-white transition"
            >
              Baca Blog & Tips UMKM
            </Link>
          </div>

          {/* Other cities */}
          <div className="mt-16 pt-10 border-t border-[var(--brand-border)]">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--brand-gray-500)] font-semibold">
              Melayani Juga Kota Lain
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {cities
                .filter((c) => c.slug !== city.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/jasa-website-${c.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[var(--brand-border)] px-4 py-2 text-sm font-medium text-[var(--brand-ink)] hover:bg-[var(--brand-ink)] hover:text-white hover:border-[var(--brand-ink)] transition"
                  >
                    <MapPin className="w-3 h-3" /> {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWA />
    </div>
  );
}
